/* eslint-disable camelcase */
import React, { useState, useEffect } from "react"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  Divider,
  Badge,
  Avatar,
  InputAdornment,
  Chip,
  Alert,
} from "@mui/material"
import {
  CloudOff,
  CloudDone,
  Sync,
  Search,
  Phone,
  CheckCircle,
  Cancel,
  AssignmentInd,
  AccessTime,
  Star,
  Public,
} from "@mui/icons-material"
import localforage from "localforage"
import Swal from "sweetalert2"
import dayjs from "dayjs"

import Header from "../../../../layouts/Header"
import BackIcon from "../../../../components/BackIcon"
import { api } from "../../../../api"

// Tema SIAC Táctico
const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#ed6c02" },
    success: { main: "#2E7D32" },
    error: { main: "#d32f2f" },
  },
})

const ControlAsistenciaRecinto = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [isLoading, setIsLoading] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)

  // Estados de datos
  const [delegados, setDelegados] = useState([])
  const [filtroTexto, setFiltroTexto] = useState("")
  const [colaAsistencia, setColaAsistencia] = useState([])
  const [errorAcceso, setErrorAcceso] = useState(null)
  const [recintoActual, setRecintoActual] = useState("")
  const [esGlobal, setEsGlobal] = useState(false)

  // ========================================================
  // 1. INICIALIZACIÓN
  // ========================================================
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    cargarListaDelegados()
    verificarColaLocal()

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  const verificarColaLocal = async () => {
    const cola = (await localforage.getItem("asistencia_offline_queue")) || []
    setColaAsistencia(cola)
  }

  const cargarListaDelegados = async () => {
    setIsLoading(true)
    setErrorAcceso(null)
    try {
      if (isOffline) throw new Error("Offline")

      const res = await api.post("/ControlElectoral/getAsistenciaCoordinador", {})

      if (res.data && res.data.success === false) {
        setErrorAcceso(res.data.message)
        return
      }

      const delegadosReales = res.data.data || []
      const recintoAsignado = res.data.recinto || ""
      const isGlobalBackend = res.data.es_global || false

      setDelegados(delegadosReales)
      setRecintoActual(recintoAsignado)
      setEsGlobal(isGlobalBackend)

      await localforage.setItem("coordinador_delegados_cache", delegadosReales)
      await localforage.setItem("coordinador_recinto_cache", recintoAsignado)
      await localforage.setItem("coordinador_global_cache", isGlobalBackend)
    } catch (error) {
      console.log("Cargando delegados desde caché offline...", error)
      if (error?.response?.status === 403) {
        setErrorAcceso(error.response.data.message || "Usuario no autorizado.")
      } else {
        const cache = (await localforage.getItem("coordinador_delegados_cache")) || []
        const cacheRecinto = (await localforage.getItem("coordinador_recinto_cache")) || ""
        const cacheGlobal = (await localforage.getItem("coordinador_global_cache")) || false
        setDelegados(cache)
        setRecintoActual(cacheRecinto)
        setEsGlobal(cacheGlobal)
      }
    } finally {
      setIsLoading(false)
    }
  }

  // ========================================================
  // 2. LÓGICA DE CHECK-IN TÁCTICO
  // ========================================================
  const handleToggleAsistencia = async (del_codigo, estadoActual) => {
    const nuevoEstado = estadoActual === 1 ? 0 : 1
    const horaMarcada = nuevoEstado === 1 ? dayjs().format("hh:mm A") : null

    const delegadosActualizados = delegados.map((d) =>
      d.del_codigo === del_codigo ? { ...d, asistencia: nuevoEstado, hora_llegada: horaMarcada } : d,
    )
    setDelegados(delegadosActualizados)
    await localforage.setItem("coordinador_delegados_cache", delegadosActualizados)

    const payload = {
      del_codigo,
      asistencia: nuevoEstado,
      timestamp: new Date().toISOString(),
    }

    if (isOffline) {
      const cola = (await localforage.getItem("asistencia_offline_queue")) || []
      const colaFiltrada = cola.filter((item) => item.del_codigo !== del_codigo)
      colaFiltrada.push(payload)
      await localforage.setItem("asistencia_offline_queue", colaFiltrada)
      setColaAsistencia(colaFiltrada)
    } else {
      try {
        await api.post("/ControlElectoral/marcarAsistencia", payload)
      } catch (error) {
        const cola = (await localforage.getItem("asistencia_offline_queue")) || []
        cola.push(payload)
        await localforage.setItem("asistencia_offline_queue", cola)
        setColaAsistencia(cola)
      }
    }
  }

  // ========================================================
  // 3. SINCRONIZACIÓN MANUAL
  // ========================================================
  const sincronizarCola = async () => {
    if (isOffline) return Swal.fire("Sin señal", "Acérquese a una ventana para sincronizar.", "warning")
    const cola = (await localforage.getItem("asistencia_offline_queue")) || []
    if (cola.length === 0) return

    setIsSyncing(true)
    let colaRestante = [...cola]

    for (let i = 0; i < cola.length; i++) {
      try {
        await api.post("/ControlElectoral/marcarAsistencia", cola[i])
        colaRestante = colaRestante.filter((c) => c.del_codigo !== cola[i].del_codigo)
      } catch (err) {
        console.error("Fallo al subir asistencia", err)
      }
    }

    await localforage.setItem("asistencia_offline_queue", colaRestante)
    setColaAsistencia(colaRestante)
    setIsSyncing(false)

    if (colaRestante.length === 0) {
      Swal.fire("Sincronizado", "Listado actualizado.", "success")
    }
  }

  const delegadosFiltrados = delegados.filter(
    (d) =>
      (d.nombre && d.nombre.toLowerCase().includes(filtroTexto.toLowerCase())) ||
      (d.mesa && d.mesa.toLowerCase().includes(filtroTexto.toLowerCase())) ||
      (d.rol && d.rol.toLowerCase().includes(filtroTexto.toLowerCase())),
  )

  const totalPresentes = delegados.filter((d) => d.asistencia === 1).length
  const totalAusentes = delegados.length - totalPresentes

  return (
    <ThemeProvider theme={theme}>
      <Header />

      <div className="main main-app p-2" style={{ backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
        <BackIcon />

        {/* BARRA SUPERIOR DE RED Y SYNC */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          p={2}
          bgcolor="white"
          borderRadius={2}
          boxShadow={1}
        >
          <Box display="flex" alignItems="center">
            {isOffline ? <CloudOff color="error" sx={{ mr: 1 }} /> : <CloudDone color="success" sx={{ mr: 1 }} />}
            <Typography variant="body2" fontWeight="bold" color={isOffline ? "error" : "success.main"}>
              {isOffline ? "MODO OFFLINE" : "CONEXIÓN ESTABLE"}
            </Typography>
          </Box>
          <Badge badgeContent={colaAsistencia.length} color="secondary">
            <IconButton
              onClick={sincronizarCola}
              disabled={colaAsistencia.length === 0 || isOffline}
              sx={{ bgcolor: "#e3f2fd" }}
            >
              <Sync color={colaAsistencia.length > 0 ? "primary" : "disabled"} />
            </IconButton>
          </Badge>
        </Box>

        <Typography variant="h5" color="primary" fontWeight="bold" mb={1} display="flex" alignItems="center">
          <AssignmentInd sx={{ mr: 1, fontSize: 32 }} /> Control de Asistencia
        </Typography>

        {/* Muestra Cobertura Local o Global */}
        {recintoActual && (
          <Box display="flex" alignItems="center" mb={2}>
            <Typography variant="subtitle1" color="textSecondary" fontWeight="bold" display="flex" alignItems="center">
              📍 Cobertura: {recintoActual}
            </Typography>
            {esGlobal && (
              <Chip
                icon={<Public fontSize="small" />}
                label="MODO GLOBAL"
                color="info"
                size="small"
                sx={{ ml: 2, fontWeight: "bold" }}
              />
            )}
          </Box>
        )}

        {errorAcceso ? (
          <Alert severity="error" variant="filled" sx={{ mt: 4, borderRadius: 2, p: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              ACCESO RESTRINGIDO
            </Typography>
            <Typography variant="body1">{errorAcceso}</Typography>
          </Alert>
        ) : (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6}>
                <Paper
                  elevation={2}
                  sx={{ p: 2, borderRadius: 3, textAlign: "center", borderTop: "4px solid #2E7D32" }}
                >
                  <Typography variant="h3" fontWeight="bold" color="success.main">
                    {totalPresentes}
                  </Typography>
                  <Typography variant="caption" fontWeight="bold" color="textSecondary">
                    ASISTIERON
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper
                  elevation={2}
                  sx={{ p: 2, borderRadius: 3, textAlign: "center", borderTop: "4px solid #d32f2f" }}
                >
                  <Typography variant="h3" fontWeight="bold" color="error.main">
                    {totalAusentes}
                  </Typography>
                  <Typography variant="caption" fontWeight="bold" color="textSecondary">
                    AUSENTES
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* BUSCADOR */}
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar por nombre, recinto o rol..."
              value={filtroTexto}
              onChange={(e) => setFiltroTexto(e.target.value)}
              sx={{ mb: 3, bgcolor: "white", borderRadius: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="primary" />
                  </InputAdornment>
                ),
              }}
            />

            {/* LISTADO DE DELEGADOS */}
            <Grid container spacing={2} pb={4}>
              {delegadosFiltrados.map((del) => {
                const isCoordinador = del.rol === "COORDINADOR"

                return (
                  <Grid item xs={12} sm={6} md={4} key={del.del_codigo}>
                    <Card
                      elevation={isCoordinador ? 4 : 1}
                      sx={{
                        borderRadius: 3,
                        borderLeft: del.asistencia === 1 ? "6px solid #2E7D32" : "6px solid #bdbdbd",
                        bgcolor: isCoordinador ? "#f0f8ff" : "white",
                      }}
                    >
                      <CardContent sx={{ p: "16px !important" }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                          <Box display="flex" alignItems="center">
                            <Avatar
                              sx={{
                                bgcolor: del.asistencia === 1 ? "#e8f5e9" : "#f5f5f5",
                                color: del.asistencia === 1 ? "#2E7D32" : "#9e9e9e",
                                mr: 2,
                                fontWeight: "bold",
                              }}
                            >
                              {del.nombre ? del.nombre.charAt(0) : "D"}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
                                {del.nombre}
                              </Typography>
                              {esGlobal && (
                                <Typography variant="caption" color="primary.main" fontWeight="bold" display="block">
                                  Recinto: {del.mesa}
                                </Typography>
                              )}
                              <Chip
                                label={del.rol || "DELEGADO"}
                                size="small"
                                icon={isCoordinador ? <Star fontSize="small" /> : undefined}
                                color={isCoordinador ? "primary" : "default"}
                                sx={{ mt: 0.5, fontWeight: "bold", fontSize: "0.65rem", height: "20px" }}
                              />
                            </Box>
                          </Box>

                          {del.telefono && (
                            <IconButton
                              component="a"
                              href={`tel:${del.telefono}`}
                              color="primary"
                              sx={{ bgcolor: "#e3f2fd" }}
                            >
                              <Phone />
                            </IconButton>
                          )}
                        </Box>

                        <Divider sx={{ mb: 2, mt: 1 }} />

                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          {del.asistencia === 1 ? (
                            <Box display="flex" alignItems="center">
                              <CheckCircle color="success" sx={{ mr: 0.5 }} />
                              <Typography variant="body2" color="success.main" fontWeight="bold">
                                PRESENTE
                              </Typography>
                            </Box>
                          ) : (
                            <Box display="flex" alignItems="center">
                              <Cancel color="disabled" sx={{ mr: 0.5 }} />
                              <Typography variant="body2" color="textSecondary" fontWeight="bold">
                                NO LLEGA
                              </Typography>
                            </Box>
                          )}

                          <Button
                            variant={del.asistencia === 1 ? "outlined" : "contained"}
                            color={del.asistencia === 1 ? "error" : "primary"}
                            onClick={() => handleToggleAsistencia(del.del_codigo, del.asistencia)}
                            sx={{ borderRadius: 2, fontWeight: "bold", minWidth: "120px" }}
                          >
                            {del.asistencia === 1 ? "ANULAR" : "MARCAR LLEGADA"}
                          </Button>
                        </Box>

                        {del.asistencia === 1 && del.hora_llegada && (
                          <Typography
                            variant="caption"
                            display="flex"
                            alignItems="center"
                            color="textSecondary"
                            mt={1.5}
                          >
                            <AccessTime sx={{ fontSize: 14, mr: 0.5 }} /> Ingresó a las: {del.hora_llegada}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )
              })}

              {delegadosFiltrados.length === 0 && !isLoading && (
                <Box width="100%" textAlign="center" mt={4}>
                  <Typography color="textSecondary" fontStyle="italic">
                    No se encontró personal logístico.
                  </Typography>
                </Box>
              )}
            </Grid>
          </>
        )}
      </div>
    </ThemeProvider>
  )
}

export default ControlAsistenciaRecinto
